import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { NOTICE_TYPE_LABELS } from '../../lib/constants';
import type { NoticeType, Page, PublicNotice } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function StaffNotices() {
  const { persona } = useAuth();
  const isSteward = (persona?.roles ?? []).some((r) => r.startsWith('DATA_STEWARD:'));
  const [showPublish, setShowPublish] = useState(false);
  const noticesQ = useApiQuery<Page<PublicNotice>>('/api/v1/opengov/notices?limit=100');

  return (
    <div>
      <PageHeader
        title="Public notices"
        subtitle="Publish a gazette notice, tender, planning application or licence to the public notices board."
        actions={
          isSteward ? (
            <button className="btn-primary" onClick={() => setShowPublish(true)}>
              + Publish notice
            </button>
          ) : undefined
        }
      />

      {!isSteward ? (
        <div className="mb-4">
          <ErrorNote message="Your account does not hold a DATA_STEWARD role — publishing will be rejected by the API." />
        </div>
      ) : null}

      {noticesQ.error ? <ErrorNote message={noticesQ.error} /> : null}
      <div className="card divide-y divide-slate-100">
        {noticesQ.loading ? (
          <Spinner />
        ) : (noticesQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No notices published yet" />
        ) : (
          noticesQ.data!.items.map((n) => (
            <div key={n.id} className="flex items-start justify-between gap-4 px-4 py-3.5">
              <div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={n.type} />
                  <span className="text-xs text-slate-400">{n.sourceModule}</span>
                </div>
                <div className="mt-1 font-semibold text-slate-900">
                  {typeof n.payload.title === 'string' ? n.payload.title : `${n.type} notice`}
                </div>
              </div>
              <div className="shrink-0 text-xs text-slate-400">{formatDate(n.publishedAt)}</div>
            </div>
          ))
        )}
      </div>

      {showPublish ? (
        <PublishNoticeModal
          onClose={() => setShowPublish(false)}
          onDone={() => {
            setShowPublish(false);
            noticesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PublishNoticeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<NoticeType>('GAZETTE');
  const [sourceModule, setSourceModule] = useState('opengov-staff-console');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [reference, setReference] = useState('');
  const [closingDate, setClosingDate] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const referenceLabel =
    type === 'GAZETTE' ? 'Gazette number' : type === 'TENDER' ? 'Tender number' : type === 'LICENCE' ? 'Licence number' : 'Reference';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { title: title.trim(), summary: summary.trim() };
      if (reference.trim()) {
        payload[type === 'GAZETTE' ? 'gazetteNo' : type === 'TENDER' ? 'tenderNo' : type === 'LICENCE' ? 'licenceNo' : 'reference'] =
          reference.trim();
      }
      if (type === 'TENDER' && closingDate) payload.closingDate = closingDate;

      await api.post('/api/v1/opengov/notices', {
        type,
        sourceModule: sourceModule.trim(),
        payload,
        ...(publishedAt ? { publishedAt: new Date(publishedAt).toISOString() } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to publish notice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Publish notice" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as NoticeType)}>
              {Object.entries(NOTICE_TYPE_LABELS).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Source module">
            <input className="field-input" value={sourceModule} onChange={(e) => setSourceModule(e.target.value)} />
          </Field>
        </div>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Summary">
          <textarea className="field-input" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`${referenceLabel} (optional)`}>
            <input className="field-input" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          {type === 'TENDER' ? (
            <Field label="Closing date (optional)">
              <input
                className="field-input"
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </Field>
          ) : (
            <Field label="Publish date (optional — defaults to now)">
              <input
                className="field-input"
                type="date"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
              />
            </Field>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !title.trim() || !sourceModule.trim()}>
            {busy ? 'Publishing…' : 'Publish notice'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
